/** Public form submissions and the admin views over them. */
import { apiBlob, apiRequest } from './client';
import type {
  AdminStats,
  QAEngineerCredentials,
  QAEngineerSubmission,
  SubmissionStatus,
} from './types';

/** Pagination envelope the admin list endpoints wrap their rows in. */
interface Paged {
  total: number;
  totalPages: number;
  page?: number;
}

export type ListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  /**
   * ISO-2 country codes, comma separated. Absent or empty means every country.
   *
   * Only `/admin/qa-engineers` reads it -- employers and contacts do not record
   * a country of residence -- but it lives on the shared params type because the
   * three lists share one client shape.
   */
  countries?: string;
  /** `exclude` inverts `countries`; anything else keeps it as a keep-list. */
  countriesMode?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export const submissionsApi = {
  /* ---- public ---- */

  /**
   * Whether this submission comes from the panel rather than the public site.
   *
   * It decides the *credential*, not a field in the body. The server used to
   * read `adminCreated` out of the JSON, which meant anyone could claim it and
   * skip the anti-spam and rate-limit checks; it now derives the same fact from
   * the Authorization header. So a panel submission has to carry the admin
   * session, and this flag is what makes it do so.
   */
  submitQAEngineer: (payload: Record<string, unknown>, options: { asAdmin?: boolean } = {}) =>
    apiRequest<{ success: boolean; id?: string; message?: string }>('/submit/qa-engineer', {
      method: 'POST',
      body: payload,
      auth: options.asAdmin ? 'admin' : 'public',
    }),

  submitEmployer: (payload: Record<string, unknown>, options: { asAdmin?: boolean } = {}) =>
    /*
     * `duplicate: true` means the server already had this exact enquiry from the
     * last ten minutes and returned its original id without storing or emailing
     * anything. The forms show the thank-you and fire no lead events for it.
     */
    apiRequest<{ success: boolean; id?: string; message?: string; duplicate?: boolean }>('/submit/employer', {
      method: 'POST',
      body: payload,
      auth: options.asAdmin ? 'admin' : 'public',
    }),

  /*
   * No admin variant: the panel never creates a contact record, so this one is
   * always the public credential. Stated rather than left to `apiRequest`'s
   * default, so that all three submit calls name the credential they travel
   * with -- the thing that decides whether the anti-automation checks apply.
   */
  submitContact: (payload: Record<string, unknown>) =>
    apiRequest<{ success: boolean; id?: string; message?: string }>('/submit/contact', {
      method: 'POST',
      body: payload,
      auth: 'public',
    }),

  /*
   * Step one of the booking brief: the server screens the brief and mails a
   * code to its address. `submitContact` then sends the same brief with
   * `verification: { token, code }`; without it the server refuses the brief.
   */
  requestContactCode: (payload: Record<string, unknown>) =>
    apiRequest<{ success: boolean; token: string; expiresAt: number }>('/submit/contact/code', {
      method: 'POST',
      body: payload,
      auth: 'public',
    }),

  /* ---- admin ---- */

  stats: <T = AdminStats>() => apiRequest<{ stats: T }>('/admin/stats'),

  /*
   * Employer and contact rows are generic: the edge function assembles each
   * view's fields inline and there is no single shape worth pinning here, so the
   * admin page that reads them supplies its own.
   */
  employers: <T = Record<string, unknown>>(params: ListParams = {}, signal?: AbortSignal) =>
    apiRequest<Paged & { employers: T[] }>('/admin/employers', {
      query: params,
      signal,
    }),

  contacts: <T = Record<string, unknown>>(params: ListParams = {}, signal?: AbortSignal) =>
    apiRequest<Paged & { contacts: T[] }>('/admin/contacts', {
      query: params,
      signal,
    }),

  qaEngineers: (params: ListParams = {}, signal?: AbortSignal) =>
    apiRequest<Paged & { qaEngineers: QAEngineerSubmission[] }>('/admin/qa-engineers', {
      query: params,
      signal,
    }),

  /**
   * The stored record, including the credentials block the list omits.
   *
   * Note this is the raw record, not the snake_case view `qaEngineers` returns:
   * only the credentials fields are typed, because they are the only ones any
   * caller reads from here.
   */
  qaEngineerDetails: (id: string) =>
    apiRequest<{ qaEngineer: QAEngineerCredentials }>(
      `/admin/qa-engineers/${encodeURIComponent(id)}/details`
    ),

  updateQAEngineer: (id: string, patch: Partial<QAEngineerSubmission>) =>
    apiRequest<{ success: boolean }>(`/admin/qa-engineers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: patch,
    }),

  updateQAEngineerCredentials: (id: string, credentials: QAEngineerCredentials) =>
    apiRequest<{ success: boolean }>(
      `/admin/qa-engineers/${encodeURIComponent(id)}/credentials`,
      { method: 'PUT', body: credentials }
    ),

  setQAEngineerStatus: (id: string, status: SubmissionStatus | string, notes?: string) =>
    apiRequest<{ success: boolean }>(`/admin/qa-engineers/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: { status, notes },
    }),

  removeQAEngineer: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/qa-engineers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** Employer and contact records share one submissions endpoint. */
  setSubmissionStatus: (id: string, status: SubmissionStatus | string, notes?: string) =>
    apiRequest<{ success: boolean }>(`/admin/submissions/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      body: { status, notes },
    }),

  removeSubmission: (id: string) =>
    apiRequest<{ success: boolean }>(`/admin/submissions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  bulkRemoveSubmissions: (ids: string[]) =>
    apiRequest<{ success: boolean; deleted: number }>('/admin/submissions/bulk-delete', {
      method: 'DELETE',
      body: { ids },
    }),

  /**
   * A candidate's resume, as a Blob.
   *
   * Fetched rather than linked: the endpoint is behind the admin allow list and
   * Supabase's gateway requires the Authorization header, which a plain <a>
   * navigation would not send.
   */
  downloadResume: (id: string) => apiBlob(`/download/resume/${encodeURIComponent(id)}`),
};
